import { Tooltip } from 'antd'
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { useAttributeProcessingContext } from '../../../../AttributeProcessingContext'
import cn from './CategoriesTab.module.scss'

const MAX_STAGGER_STEPS = 10

function CategoriesTab() {
  const { isRightPanelDisabled, selectedPreviewRows } =
    useAttributeProcessingContext()
  const groupOrderRef = useRef(new Map<string, number>())
  const [copiedInfo, setCopiedInfo] = useState<{
    token: number
    x: number
    y: number
  } | null>(null)

  const handleCopyArticle = (article: string, target: HTMLElement) => {
    const rect = target.getBoundingClientRect()
    navigator.clipboard.writeText(article).then(() => {
      const token = Date.now()
      setCopiedInfo({ token, x: rect.left + rect.width / 2, y: rect.bottom })
      setTimeout(() => {
        setCopiedInfo((current) => (current?.token === token ? null : current))
      }, 1500)
    })
  }

  useEffect(() => {
    if (!copiedInfo) return

    const hideOnScroll = () => setCopiedInfo(null)
    window.addEventListener('scroll', hideOnScroll, {
      capture: true,
      passive: true,
    })

    return () => {
      window.removeEventListener('scroll', hideOnScroll, true)
    }
  }, [copiedInfo])

  if (isRightPanelDisabled) {
    return (
      <div className={cn.tabFrame}>
        <div className={cn.infoSection}>
          <div className={cn.rightPanelPlaceholder}>
            <span>Выберите номенклатуры в левой таблице</span>
          </div>
        </div>
      </div>
    )
  }

  const groupsByCategory = new Map<string, typeof selectedPreviewRows>()
  selectedPreviewRows.forEach((row) => {
    const key = row.categoryName || 'Без категории'
    const group = groupsByCategory.get(key)
    if (group) {
      group.push(row)
    } else {
      groupsByCategory.set(key, [row])
    }
  })

  const groups = Array.from(groupsByCategory.entries()).map(
    ([categoryName, items]) => ({
      categoryName,
      items,
    })
  )

  groups.forEach((group) => {
    if (!groupOrderRef.current.has(group.categoryName)) {
      groupOrderRef.current.set(group.categoryName, groupOrderRef.current.size)
    }
  })

  return (
    <div className={cn.tabFrame}>
      <div className={cn.infoSection}>
        <div className={cn.summaryBar}>
          <span>
            Категорий: <strong>{groups.length}</strong>
          </span>
          <span>
            Товаров: <strong>{selectedPreviewRows.length}</strong>
          </span>
        </div>

        <div className={cn.categoriesList}>
          {groups.map((group) => {
            const slot = groupOrderRef.current.get(group.categoryName) ?? 0
            return (
              <div
                key={group.categoryName}
                className={cn.categoryGroup}
                style={{
                  '--stagger-delay': `${Math.min(slot, MAX_STAGGER_STEPS) * 30}ms`,
                }}
              >
                <div className={cn.categoryGroupHeader}>
                  <span className={cn.categoryGroupName}>
                    {group.categoryName}
                  </span>
                  <Tooltip
                    title="Товаров в категории"
                    placement="top"
                    mouseEnterDelay={0.3}
                  >
                    <span className={cn.categoryGroupCount}>
                      {group.items.length}
                    </span>
                  </Tooltip>
                </div>
                <div className={cn.categoryGroupItems}>
                  {group.items.map((row) => (
                    <div key={row.id} className={cn.categoryItem}>
                      <span className={cn.categoryItemName}>
                        {row.description}
                      </span>
                      {row.article && (
                        <span
                          className={cn.categoryItemArticle}
                          onClick={(e) => {
                            e.stopPropagation()
                            handleCopyArticle(row.article, e.currentTarget)
                          }}
                          title="Скопировать артикул"
                        >
                          {row.article}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {copiedInfo &&
        createPortal(
          <div
            key={copiedInfo.token}
            className={cn.copyNotification}
            style={{
              '--copy-x': `${copiedInfo.x}px`,
              '--copy-y': `${copiedInfo.y}px`,
            }}
          >
            скопировано
          </div>,
          document.body
        )}
    </div>
  )
}

export default CategoriesTab
